import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import {ActivatedRouteSnapshot, RouterStateSnapshot, Router, CanActivate} from '@angular/router';
import {LocalStorageService} from '../local-storage.service';
import {AuthenticationService} from '../authentication.service';
import {isJwtExpired} from './jwt.util';
import {NOTION_LOGIN_STATE_PREFIX} from '../../../views/string-constants';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate  {
  constructor(
    private router: Router,
    private storageService: LocalStorageService,
    private authenticationService: AuthenticationService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    // During SSR/prerender there is no localStorage; let the route render its
    // own neutral shell — the guard re-runs in the browser as usual.
    if (isPlatformServer(this.platformId)) {
      return true;
    }

    const token = this.storageService.getItem('token');
    // Checked directly on the stored token, not via AuthenticationService: the
    // guard must not depend on that service's constructor having run first.
    if (token && !isJwtExpired(token)) {
        return true;
    }

    // Notion sign-in callback: the component will exchange the code for a JWT.
    // Allow through so it can handle the code+state without losing them.
    const code = route.queryParamMap.get('code');
    const notionState = route.queryParamMap.get('state') ?? '';
    if (code && notionState.startsWith(NOTION_LOGIN_STATE_PREFIX)) {
      return true;
    }

    // Denied consent on the "Sign in with Notion" flow. Notion answers a
    // cancelled consent screen with `?error=access_denied&state=...` and no
    // `code`. On this flow the caller is still unauthenticated, so without
    // this branch the generic redirect below bounced it to /login.
    // OauthComponent never ran, so the denial notice it owns was unreachable
    // while the login nonce plus the in-flight connect flag stayed behind.
    // The `nh2-login.` state is what separates this from a connect-flow
    // denial, which is already authenticated so it renders the notice on
    // /start itself. Forwarded to /register rather than /login
    // because that is where the "Continue with Notion" button lives, so the
    // retry the notice offers is one click away. Any `error` value counts;
    // `access_denied` is the only one Notion is known to send.
    const error = route.queryParamMap.get('error');
    if (error && !code && notionState.startsWith(NOTION_LOGIN_STATE_PREFIX)) {
      // The same two keys OauthComponent.handleOauthDenial clears, cleared
      // the same way. The flag is set to the string 'false', not removed.
      this.storageService.removeItem('notionLoginNonce');
      this.storageService.setItem('oauthProcessStarted', 'false');
      // Deliberately no `redirectUrl`: a /start URL carrying a dead denial is
      // not worth replaying after a later login.
      this.router.navigate(['/register'], {
        replaceUrl: true,
        queryParams: {notion: 'denied'},
      });
      return false;
    }

    // Store the attempted URL for redirecting after login
    this.authenticationService.redirectUrl = state.url;
    // /feedback is the one guarded target that is also linked from public
    // places (old bookmarks, shared links, the roadmap CTA), so /login explains
    // the detour and offers email. Matched on the path alone, query and
    // fragment ignored. Other guarded routes get a bare /login.
    const isFeedback = state.url.split(/[?#]/)[0] === '/feedback';
    this.router.navigate(['/login'], {
      replaceUrl: true,
      ...(isFeedback ? {queryParams: {reason: 'feedback'}} : {}),
    });
    return false;
  }
}
